import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";

const CARD = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
  padding: "20px",
  boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
};

const DIVIDER = { height: "1px", backgroundColor: "#F1F5F9", margin: "12px 0" };

const BTN_BASE = "h-[44px] rounded-md text-[14px] font-[600] text-white transition-all duration-200";

const CaseInformation = ({
  selectedCase,
  modelInfos,
  handleSTLFileSelect,
  handleDICOMFileSelect,
  handleViewerClick,
  handleEditClick,
  handleCommentCase,
  handleShare,
  handleUserPermissions,
  userRole,
  isDemo = false,
  isMobile,
}) => {
  if (!selectedCase) return <p style={{ color: "#94A3B8" }}>Loading case information...</p>;

  const formatExpiration = (exp) => (!exp || exp === "Invalid Date" ? "" : exp);
  const { title, anatomy, creator, creationDate, expirationDate, dueDate } = selectedCase;

  const canEdit = ["Admin", "Creator", "Owner", "Editor"].includes(userRole) && !isDemo;
  const canPerm = ["Admin", "Creator", "Owner"].includes(userRole) && !isDemo;
  const canView = ["Admin", "Creator", "Owner", "Editor", "Viewer"].includes(userRole);

  const Field = ({ label, value }) => (
    <p className="leading-7">
      <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569", marginRight: "4px" }}>{label}:</span>
      <span style={{ fontSize: "14px", fontWeight: 400, color: "#0F172A" }}>{value}</span>
    </p>
  );

  const Placeholder = () => <div className="h-[44px] rounded-md" />;

  const btnHover = (base, bg, hoverBg) => ({
    style: { backgroundColor: bg },
    onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = hoverBg; },
    onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = bg; },
    className: `${BTN_BASE} w-full`,
  });

  // ——— MOBILE ———
  if (isMobile) {
    return (
      <div style={CARD}>
        <div className="flex items-center justify-between">
          <h2 style={{ fontSize: "18px", fontWeight: 800, color: "#0F172A" }}>Case Information</h2>
          <button
            onClick={handleShare}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full transition"
            style={{ backgroundColor: "#F1F5F9" }}
            aria-label="Share"
          >
            <ShareOutlinedIcon fontSize="small" style={{ color: "#475569" }} />
          </button>
        </div>
        <div style={DIVIDER} />
        <div className="space-y-1">
          <Field label="Name" value={title} />
          <Field label="Anatomy" value={anatomy} />
          <Field label="Creator" value={creator} />
          <Field label="Creation Date" value={creationDate} />
          <Field label="Due Date" value={dueDate} />
          <Field label="Expiry Date" value={formatExpiration(expirationDate)} />
        </div>
        <div style={DIVIDER} />
        <div className="grid grid-cols-2 gap-3 pt-1">
          {canView && modelInfos?.length > 0 ? (
            <button onClick={handleViewerClick} type="button" {...btnHover(BTN_BASE + " w-full", "#2864F0", "#1a4fd8")}>Open in 3D Viewer</button>
          ) : <Placeholder />}
          {canEdit ? (
            <button onClick={handleCommentCase} type="button" {...btnHover(BTN_BASE + " w-full", "#30A69C", "#228880")}>Comment Case</button>
          ) : <Placeholder />}
          {canEdit ? (
            <label className={`${BTN_BASE} w-full cursor-pointer flex items-center justify-center`} style={{ backgroundColor: "#1796D0" }}>
              Upload STL<input type="file" accept=".stl" multiple className="hidden" onChange={handleSTLFileSelect} />
            </label>
          ) : <Placeholder />}
          {canEdit ? (
            <button onClick={handleEditClick} type="button" {...btnHover(BTN_BASE + " w-full", "#168741", "#0f5e2e")}>Edit Case</button>
          ) : <Placeholder />}
          {canEdit ? (
            <label className={`${BTN_BASE} w-full cursor-pointer flex items-center justify-center`} style={{ backgroundColor: "#1796D0" }}>
              Upload DICOM (ZIP)<input type="file" accept=".zip,.rar" multiple className="hidden" onChange={handleDICOMFileSelect} />
            </label>
          ) : <Placeholder />}
          {canPerm ? (
            <button onClick={handleUserPermissions} type="button" {...btnHover(BTN_BASE + " w-full", "#247877", "#1a5655")}>User Permissions</button>
          ) : <Placeholder />}
        </div>
      </div>
    );
  }

  // ——— DESKTOP ———
  return (
    <>
      <div className="flex justify-between items-center mb-3">
        <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#0F172A" }}>Case Information</h2>
        <button
          onClick={handleShare}
          className="flex items-center font-semibold transition-colors duration-200"
          style={{ fontSize: "18px", color: "#94A3B8" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#0F172A"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#94A3B8"; }}
          aria-label="Share case"
        >
          Share <ShareOutlinedIcon className="ml-1" fontSize="inherit" />
        </button>
      </div>

      <div style={CARD}>
        <div className="flex flex-row md:flex-col items-start">
          <div className="flex-shrink-0 space-y-1">
            {[
              { label: "Name", value: title },
              { label: "Anatomy", value: anatomy },
              { label: "Creator", value: creator },
              { label: "Creation Date", value: creationDate },
              { label: "Due Date", value: dueDate },
              { label: "Expiry Date", value: formatExpiration(expirationDate) },
            ].map(({ label, value }) => (
              <p key={label} style={{ fontSize: "17px" }}>
                <span style={{ fontWeight: 500, color: "#475569", marginRight: "4px" }}>{label}:</span>
                <span style={{ fontWeight: 400, color: "#0F172A" }}>{value}</span>
              </p>
            ))}
          </div>
          <div className="flex-1" />
          <div className="grid grid-cols-2 gap-4">
            {[
              canEdit && <button key="comment" onClick={handleCommentCase} type="button" className="w-[200px] h-[50px] text-[15px] font-[600] text-white rounded-lg transition-all" style={{ background: "linear-gradient(160deg, #35b5aa 0%, #249990 100%)", boxShadow: "0 4px 14px rgba(48,164,151,0.35)", border: "none" }} onMouseEnter={(e) => e.currentTarget.style.background="linear-gradient(160deg, #249990 0%, #1a7068 100%)"} onMouseLeave={(e) => e.currentTarget.style.background="linear-gradient(160deg, #35b5aa 0%, #249990 100%)"}> Comment Case </button>,
              (canView && modelInfos?.length > 0) && <button key="viewer" onClick={handleViewerClick} type="button" className="w-[200px] h-[50px] text-[15px] font-[600] text-white rounded-lg transition-all" style={{ background: "linear-gradient(160deg, #4a87f5 0%, #2660e8 100%)", boxShadow: "0 4px 14px rgba(38,96,232,0.35)", border: "none" }} onMouseEnter={(e) => e.currentTarget.style.background="linear-gradient(160deg, #2660e8 0%, #1a4ac5 100%)"} onMouseLeave={(e) => e.currentTarget.style.background="linear-gradient(160deg, #4a87f5 0%, #2660e8 100%)"}> Open in 3D Viewer </button>,
              canEdit && <button key="edit" onClick={handleEditClick} type="button" className="w-[200px] h-[50px] text-[15px] font-[600] text-white rounded-lg transition-all" style={{ background: "linear-gradient(160deg, #2ea35c 0%, #1d7a42 100%)", boxShadow: "0 4px 14px rgba(29,122,66,0.35)", border: "none" }} onMouseEnter={(e) => e.currentTarget.style.background="linear-gradient(160deg, #1d7a42 0%, #145530 100%)"} onMouseLeave={(e) => e.currentTarget.style.background="linear-gradient(160deg, #2ea35c 0%, #1d7a42 100%)"}> Edit Case </button>,
              canEdit && <label key="dicom" className="w-[200px] h-[50px] text-[15px] font-[600] text-white rounded-lg cursor-pointer flex items-center justify-center transition-all" style={{ background: "linear-gradient(160deg, #3da8d8 0%, #1d88be 100%)", boxShadow: "0 4px 14px rgba(29,136,190,0.35)", border: "none" }}>Upload DICOM (ZIP)<input type="file" accept=".zip,.rar" multiple className="hidden" onChange={handleDICOMFileSelect} /></label>,
              canPerm && <button key="perm" onClick={handleUserPermissions} type="button" className="w-[200px] h-[50px] text-[15px] font-[600] text-white rounded-lg transition-all" style={{ background: "linear-gradient(160deg, #35b5aa 0%, #1f807a 100%)", boxShadow: "0 4px 14px rgba(31,128,122,0.35)", border: "none" }} onMouseEnter={(e) => e.currentTarget.style.background="linear-gradient(160deg, #1f807a 0%, #165a56 100%)"} onMouseLeave={(e) => e.currentTarget.style.background="linear-gradient(160deg, #35b5aa 0%, #1f807a 100%)"}> User Permissions </button>,
              canEdit && <label key="stl" className="w-[200px] h-[50px] text-[15px] font-[600] text-white rounded-lg cursor-pointer flex items-center justify-center transition-all" style={{ background: "linear-gradient(160deg, #3da8d8 0%, #1d88be 100%)", boxShadow: "0 4px 14px rgba(29,136,190,0.35)", border: "none" }}>Upload STL<input type="file" accept=".stl" multiple className="hidden" onChange={handleSTLFileSelect} /></label>,
            ].map((btn, i) => btn || <div key={i} className="w-[200px] h-[50px]" />)}
          </div>
        </div>
      </div>
    </>
  );
};

export default CaseInformation;
